'use client';

import clsx from 'clsx';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import ReactPortal from '../react-portal';
import { useState, useRef, useEffect } from 'react';

export interface DrawerProps {
  portalId: string;
  children?: React.ReactNode;
  open?: boolean;
  onClose?: (value?: boolean) => void;
  headerClassName?: string;
  title?: string;
  titleClassName?: string;
  direction?: 'left' | 'right' | 'bottom';
  HeaderComponent?: React.ReactNode;
  closeButton?: React.ReactNode | string;
  drawerWrapperClassName?: string;
  hasCloseButton?: boolean;
  enableDragOnDesktop?: boolean;
  enableDragOnMobile?: boolean;
  panelClassName?: string;
}

export const Drawer = ({
  portalId,
  children,
  open,
  onClose,
  closeButton = 'Close',
  headerClassName,
  title,
  titleClassName,
  HeaderComponent,
  direction = 'left',
  drawerWrapperClassName,
  hasCloseButton = true,
  enableDragOnDesktop = false,
  enableDragOnMobile = false,
  panelClassName
}: DrawerProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Check if we're on mobile using window.matchMedia
  useEffect(() => {
    const checkMobile = () => {
      const mobileQuery = window.matchMedia('(max-width: 768px)');
      setIsMobile(mobileQuery.matches);
    };

    // Check initially
    checkMobile();

    // Add listener for window resize
    const mobileQuery = window.matchMedia('(max-width: 768px)');
    mobileQuery.addListener(checkMobile);

    return () => {
      mobileQuery.removeListener(checkMobile);
    };
  }, []);

  const isDragEnabled = isMobile ? enableDragOnMobile : enableDragOnDesktop;

  const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDragEnabled) return;

    setIsDragging(true);
    const pos = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setStartPos(pos);
    if (drawerRef.current) {
      drawerRef.current.style.transition = 'none';
    }
  };

  const handleDragMove = (e: TouchEvent | MouseEvent) => {
    if (!isDragging || !isDragEnabled) return;

    const currentPos = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const diff = currentPos - startPos;

    let translate = diff;
    if (direction === 'right') {
      translate = -diff;
    } else if (direction === 'bottom') {
      translate =
        'touches' in e ? e.touches[0].clientY - startPos : e.clientY - startPos;
    }

    // Limit the drag to only open the drawer in the correct direction
    if (
      (direction === 'left' && translate < 0) ||
      (direction === 'right' && translate < 0) ||
      (direction === 'bottom' && translate < 0)
    ) {
      translate = 0;
    }

    setCurrentTranslate(translate);
    if (drawerRef.current) {
      if (direction === 'bottom') {
        drawerRef.current.style.transform = `translateY(${translate}px)`;
      } else {
        drawerRef.current.style.transform = `translateX(${translate}px)`;
      }
    }
  };

  const handleDragEnd = () => {
    if (!isDragEnabled) return;

    setIsDragging(false);
    if (drawerRef.current) {
      drawerRef.current.style.transition = 'transform 0.3s ease-out';
      drawerRef.current.style.transform = '';
    }

    // If dragged more than 40% of the drawer width/height, close it
    const threshold =
      direction === 'bottom'
        ? drawerRef.current?.clientHeight || 0
        : drawerRef.current?.clientWidth || 0;

    if (currentTranslate > threshold * 0.4) {
      onClose?.();
    }
  };

  useEffect(() => {
    if (isDragging && isDragEnabled) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, isDragEnabled]);

  return (
    <ReactPortal wrapperId={portalId}>
      <Dialog open={open} onClose={onClose} className="relative z-30">
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden bg-primary-black/10">
            <div
              className={clsx(
                'pointer-events-none fixed inset-y-0 right-0 flex max-w-full',
                drawerWrapperClassName
              )}
            >
              <DialogPanel
                transition
                ref={drawerRef}
                onTouchStart={handleDragStart}
                onMouseDown={handleDragStart}
                className={clsx(
                  'pointer-events-auto w-screen max-w-md h-full transform transition duration-500 ease-in-out sm:duration-700',
                  panelClassName,
                  direction === 'left' && 'data-[closed]:-translate-x-full',
                  direction === 'right' && 'data-[closed]:translate-x-full',
                  direction === 'bottom' && 'data-[closed]:translate-y-full'
                )}
              >
                <div className="flex h-full flex-col bg-white shadow-xl">
                  {enableDragOnMobile && isMobile && (
                    <div className="w-[76px] h-[4px] rounded-full bg-primary mx-auto mt-[10px]" />
                  )}
                  {HeaderComponent ? (
                    HeaderComponent
                  ) : (
                    <div
                      className={clsx([
                        'p-6 flex items-center justify-between',
                        headerClassName
                      ])}
                    >
                      <DialogTitle
                        className={clsx(
                          'text-base font-semibold text-gray-900',
                          titleClassName
                        )}
                      >
                        {title}
                      </DialogTitle>
                      {hasCloseButton && (
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            onClick={() => onClose()}
                            className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500"
                          >
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">Close panel</span>
                            {closeButton}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {children}
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </ReactPortal>
  );
};
